import { useState, useRef, useEffect } from "react";
import { PlayCircle, X, Volume2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * <TutorialVideo /> — Mike's "Watch Me Walk You Through It" block.
 *
 * Drop on TOP of any page (Funnel Builder, AI Writer, etc) so members
 * can watch Mike narrate where to click — recorded with ScreenPal (free).
 * Cursor highlight + voice + screen all baked into one MP4.
 *
 * Auto-fetches the assigned video from /api/tutorials/{pageKey}. Mike uploads
 * the MP4 once via the admin Tutorials Manager and it appears here automatically.
 *
 * Usage:
 *   <TutorialVideo
 *     pageKey="funnel-builder"
 *     title="Watch Me Walk You Through The Funnel Builder"
 *     subtitle="3 minutes. Just click where I point. That easy."
 *   />
 *
 * Until Mike uploads a video for that pageKey, a friendly "coming soon" card renders.
 */
export default function TutorialVideo({
  title = "Watch Me Walk You Through It",
  subtitle = "Just click where I point. That easy.",
  pageKey = "default",
  srcOverride = null,   // optional: hardcode a URL instead of fetching
}) {
  const dismissKey = `tutorial-dismissed-${pageKey}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  const [playing, setPlaying] = useState(false);
  const [src, setSrc] = useState(srcOverride);
  const videoRef = useRef(null);

  // Auto-fetch the assigned tutorial video on mount (unless overridden)
  useEffect(() => {
    if (srcOverride) { setSrc(srcOverride); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/tutorials/${pageKey}`);
        if (!cancelled) setSrc(data?.src || null);
      } catch { if (!cancelled) setSrc(null); }
    })();
    return () => { cancelled = true; };
  }, [pageKey, srcOverride]);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
  };

  const handlePlay = () => {
    setPlaying(true);
    setTimeout(() => videoRef.current?.play(), 50);
  };

  // Dismissed → show a compact restore pill instead of the full block
  if (dismissed) {
    return (
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-4"
           data-testid={`tutorial-${pageKey}-dismissed`}>
        <button onClick={() => { setDismissed(false); try { localStorage.removeItem(dismissKey); } catch { /* ignore */ } }}
                data-testid={`tutorial-${pageKey}-restore`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.18em] hover:scale-[1.03] transition"
                style={{ background: "#FFFFFF", color: "#0F172A", border: "2px solid #2E9F3A" }}>
          <PlayCircle className="w-4 h-4"/> Watch Mike's Walk-Through
        </button>
      </div>
    );
  }

  return (
    <section className="border-b-2"
             style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)",
                      borderColor: "#2E9F3A" }}
             data-testid={`tutorial-${pageKey}`}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-10">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold mb-2"
                 style={{ background: "#EAF6FC", color: "#FFFFFF" }}>
              <Volume2 className="w-3 h-3"/> ★ MIKE WALKS YOU THROUGH IT
            </div>
            <h2 className="font-serif-display text-2xl md:text-4xl uppercase leading-tight"
                style={{ color: "#0F172A", textWrap: "balance" }}
                data-testid={`tutorial-${pageKey}-title`}>
              {title}
            </h2>
            <p className="mt-1 text-base md:text-lg" style={{ color: "#0F172A" }}>
              {subtitle}
            </p>
          </div>
          <button onClick={handleDismiss}
                  data-testid={`tutorial-${pageKey}-close`}
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:scale-110 transition"
                  style={{ background: "#FFFFFF", color: "#0F172A", border: "2px solid #2E9F3A" }}
                  aria-label="Hide walk-through">
            <X className="w-4 h-4"/>
          </button>
        </div>

        {/* Video player — has src → render <video>, no src → render "recording soon" card */}
        {src ? (
          <div className="relative rounded-2xl overflow-hidden shadow-xl"
               style={{ background: "#0F1116", border: "4px solid #2E9F3A",
                        aspectRatio: "16/9" }}>
            <video ref={videoRef}
                   src={src}
                   controls={playing}
                   playsInline
                   className="absolute inset-0 w-full h-full object-contain bg-black"
                   data-testid={`tutorial-${pageKey}-video`}/>
            {!playing && (
              <button onClick={handlePlay}
                      data-testid={`tutorial-${pageKey}-play`}
                      className="absolute inset-0 flex items-center justify-center cursor-pointer group">
                <div className="absolute inset-0"
                     style={{ background: "linear-gradient(135deg, #0F172A 0%, #2A3D6E 100%)" }}/>
                <div className="relative rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition"
                     style={{ width: 96, height: 96, background: "#2E9F3A" }}>
                  <PlayCircle className="w-14 h-14" strokeWidth={1.5} style={{ color: "#0F172A" }}/>
                </div>
              </button>
            )}
          </div>
        ) : (
          <div className="relative rounded-2xl overflow-hidden shadow-xl"
               style={{ background: "#EAF6FC", border: "4px solid #2E9F3A", aspectRatio: "16/9" }}
               data-testid={`tutorial-${pageKey}-placeholder`}>
            {/* Real funnel mockup preview — shows seniors WHAT they're building */}
            <svg viewBox="0 0 320 180" preserveAspectRatio="xMidYMid meet"
                 className="absolute inset-0 w-full h-full">
              <defs>
                <linearGradient id={`tut-${pageKey}-bg`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F172A"/>
                  <stop offset="100%" stopColor="#0F1A2E"/>
                </linearGradient>
              </defs>
              <rect width="320" height="180" fill={`url(#tut-${pageKey}-bg)`}/>
              <rect width="320" height="14" fill="#E0F2FE"/>
              <text x="160" y="10" textAnchor="middle" fontSize="6" fontFamily="Georgia" fontWeight="700"><tspan fill="#2E9F3A">FAITH</tspan><tspan fill="#F39A1F">ARAMA.COM</tspan></text>
              {/* photo circle */}
              <circle cx="40" cy="95" r="22" fill="#FFFFFF" stroke="#E8B539" strokeWidth="2"/>
              <circle cx="40" cy="90" r="8" fill="#287F8E"/>
              <path d="M22 110 Q40 95 58 110 L58 117 L22 117 Z" fill="#287F8E"/>
              {/* headline */}
              <rect x="75" y="55" width="135" height="9" rx="2" fill="#E0F2FE"/>
              <rect x="75" y="70" width="100" height="9" rx="2" fill="#E0F2FE"/>
              <rect x="75" y="88" width="90"  height="4" rx="2" fill="#FFFFFF" opacity="0.65"/>
              <rect x="75" y="96" width="120" height="4" rx="2" fill="#FFFFFF" opacity="0.5"/>
              {/* bullets */}
              <circle cx="78" cy="115" r="2" fill="#2E9F3A"/><rect x="84" y="113" width="60" height="3" rx="1" fill="#FFFFFF" opacity="0.6"/>
              <circle cx="78" cy="124" r="2" fill="#2E9F3A"/><rect x="84" y="122" width="50" height="3" rx="1" fill="#FFFFFF" opacity="0.6"/>
              <circle cx="78" cy="133" r="2" fill="#2E9F3A"/><rect x="84" y="131" width="65" height="3" rx="1" fill="#FFFFFF" opacity="0.6"/>
              {/* form card */}
              <rect x="225" y="40" width="80" height="110" rx="4" fill="#FFFFFF" stroke="#E8B539" strokeWidth="1.5"/>
              <rect x="225" y="40" width="80" height="20" rx="4" fill="#E8B539"/>
              <text x="265" y="54" textAnchor="middle" fontSize="6" fill="#0F1A2E" fontWeight="700">SIGN UP</text>
              <rect x="232" y="68" width="66" height="10" rx="1.5" fill="#FFFFFF"/>
              <rect x="232" y="82" width="66" height="10" rx="1.5" fill="#FFFFFF"/>
              <rect x="232" y="96" width="66" height="10" rx="1.5" fill="#FFFFFF"/>
              <rect x="232" y="115" width="66" height="14" rx="2" fill="#C0392B"/>
              <text x="265" y="125" textAnchor="middle" fontSize="6" fill="#fff" fontWeight="800">GET ACCESS</text>
            </svg>

            {/* Dark scrim + big play button + tiny copy on top */}
            <div className="absolute inset-0"
                 style={{ background: "linear-gradient(180deg, rgba(15,26,46,0.35) 0%, rgba(15,26,46,0.78) 100%)" }}/>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5">
              <div className="rounded-full flex items-center justify-center shadow-2xl mb-3"
                   style={{ width: 88, height: 88, background: "#2E9F3A", opacity: 0.92 }}>
                <PlayCircle className="w-14 h-14" strokeWidth={1.5} style={{ color: "#0F172A" }}/>
              </div>
              <div className="font-serif-display text-xl md:text-3xl uppercase leading-tight drop-shadow"
                   style={{ color: "#E0F2FE" }}>
                This Is What You're Building.
              </div>
              <p className="mt-2 text-sm md:text-base max-w-md leading-snug" style={{ color: "#FFFFFF" }}>
                Mike's voice walk-through drops here soon. Until then — scroll down and click around. It's all idiot-proof.
              </p>
              <div className="mt-3 px-3 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold"
                   style={{ background: "#FFFFFF", color: "#C9202E" }}>
                ★ VIDEO COMING SOON
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
